import { memo, useCallback, useMemo, useState } from "react";

import TagRow from "./TagRow";
import { getCardBorderStyle } from "@/app/application/theme";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { MeasurementsDaily } from "@/model/Application/Settings/SettingsResponseTypes";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

type Props = {
	groupedMeasurements: Record<string, Record<string, MeasurementsDaily[]>>;
	handleDialogOpen: (labMeasurementId: number, labAnalyzeId: number) => void;
	handleFieldChange: (
		id: number,
		labAnalyzeId: number,
		field: keyof MeasurementsDaily,
		value: MeasurementsDaily[keyof MeasurementsDaily],
	) => void;
	handleGroupDateChange: (
		groupName: string,
		tagId: string | null,
		field: string,
		value: string,
	) => void;
};

const GroupedMeasurements = ({
	groupedMeasurements,
	handleGroupDateChange,
	handleFieldChange,
	handleDialogOpen,
}: Props) => {
	const [sortBy, setSortBy] = useState<"name" | "tag">("name");
	const theme = useTheme();
	const { timezone } = useTime();

	// Memoize sorted groups to avoid recalculation
	const sortedGroups = useMemo(() => {
		return Object.entries(groupedMeasurements).map(([groupName, tags]) => {
			const sortedTags = Object.entries(tags).sort(
				([aKey, aVal], [bKey, bVal]) => {
					if (sortBy === "name") {
						return aKey.localeCompare(bKey);
					} else {
						// Sort by TagDescription from the first measurement in each array
						const aDesc =
							Array.isArray(aVal) && aVal.length > 0
								? (aVal[0]?.TagDescription ?? "")
								: "";
						const bDesc =
							Array.isArray(bVal) && bVal.length > 0
								? (bVal[0]?.TagDescription ?? "")
								: "";
						return aDesc.localeCompare(bDesc);
					}
				},
			);
			return { groupName, sortedTags };
		});
	}, [groupedMeasurements, sortBy]);

	const handleSortToggle = useCallback(() => {
		setSortBy((prev) => (prev === "name" ? "tag" : "name"));
	}, []);

	const handleDateChange = useCallback(
		(
			groupName: string,
			e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		) => {
			try {
				const value = (e.target as HTMLInputElement).value;
				if (!value) return;

				const date = dayjs(value).tz(timezone, true);
				if (!date.isValid()) {
					console.warn("Invalid date value:", value);
					return;
				}

				handleGroupDateChange(
					groupName,
					null,
					"TestDate",
					date.utc().toISOString(),
				);
			} catch (error) {
				console.error("Error updating group date:", error);
			}
		},
		[handleGroupDateChange, timezone],
	);

	return (
		<Box>
			{sortedGroups.map(({ groupName, sortedTags }) => {
				// Calculate disabled state for each group
				const isGroupDisabled = Object.values(
					groupedMeasurements[groupName] || {},
				).every(
					(tag) =>
						Array.isArray(tag) &&
						tag.every((measurement) => measurement.MeasurementType === 1),
				);

				// Group test date is empty by default
				const groupTestDate = "";

				return (
					<Card
						key={groupName}
						variant="outlined"
						sx={{
							borderRadius: 2,
							border: getCardBorderStyle(theme),
							boxShadow:
								theme.palette.mode === "dark"
									? "0 1px 0 0 rgba(255,255,255,0.05)"
									: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
							backgroundColor: theme.palette.background.paper,
							mb: 2,
							overflow: "hidden",
						}}
					>
						<CardContent
							sx={{
								px: 0,
								py: 0,
								"&:last-child": { pb: 0 },
							}}
						>
							<Box
								sx={{
									px: { xs: 2, sm: 2.5 },
									py: 1.5,
									borderBottom: `1px solid ${theme.palette.divider}`,
									bgcolor:
										theme.palette.mode === "dark"
											? alpha(theme.palette.common.white, 0.03)
											: "grey.50",
								}}
							>
							<Grid
								container
								spacing={1.5}
								alignItems="flex-end"
								sx={{ mb: 0 }}
							>
								<Grid item xs={12} sm={7} md={7}>
									<Stack spacing={0.75}>
										<Typography
											variant="h6"
											sx={{
												fontWeight: 800,
												color: "text.primary",
												lineHeight: 1.15,
											}}
										>
											{groupName}
										</Typography>
									</Stack>
								</Grid>
								<Grid item xs={12} sm={4} md={4}>
									<TextField
										disabled={isGroupDisabled}
										label={
											<Translate String="LabDailyGroup.GroupTestDate">
												Group Test Date
											</Translate>
										}
										type="datetime-local"
										size="small"
										value={groupTestDate}
										onChange={(e) => handleDateChange(groupName, e)}
										InputLabelProps={{ shrink: true }}
										fullWidth
										sx={{
											minWidth: 0,
											"& .MuiOutlinedInput-root": {
												borderRadius: 2.5,
												bgcolor: "background.paper",
											},
											"& .MuiInputBase-input": {
												padding: "8px 10px",
											},
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={1} md={1}>
									<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
										<Tooltip
											title={`Sort by ${sortBy === "name" ? "Tag" : "Name"}`}
										>
											<IconButton
												size="small"
												aria-label={
													sortBy === "name"
														? "Sort tags by ID"
														: "Sort tags by name"
												}
												onClick={handleSortToggle}
												sx={{
													color: "text.secondary",
													border: "1px solid",
													borderColor: "divider",
													borderRadius: 2,
													bgcolor: "background.paper",
													"&:hover": {
														color: "primary.main",
														backgroundColor: "action.hover",
													},
												}}
											>
												<SwapVertIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								</Grid>
							</Grid>
							</Box>
							<Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
							{sortedTags.map(([tagId, measurementsByTag], index) => (
								<TagRow
									key={`${tagId}-${groupName}-${index}`}
									measurementsByTag={measurementsByTag}
									tagId={tagId}
									sortBy={sortBy}
									groupName={groupName}
									handleDialogOpen={handleDialogOpen}
									handleFieldChange={handleFieldChange}
									handleGroupDateChange={handleGroupDateChange}
								/>
							))}
							</Box>
						</CardContent>
					</Card>
				);
			})}
		</Box>
	);
};

export default memo(GroupedMeasurements);
